import {useReducer} from 'react';

import {
  Handler,
  HandlerOf,
  PredicateOf,
} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ClientRequiredTaggingParameter} from '../../../../../../models/api/requiredTaggingModel';
import {RuleBundleModel} from '../../../../../../models/api/ruleBundleModel';
import {PartialRuleModelStrict} from '../../../../../../models/api/ruleModel';
import {CustomFields} from '../../../../../editors/commons/customFieldsModel';
import {RuleIntegration} from '../../../../ruleIntegrationModel';
import {EditorStateActions, useEditorState} from '../../../hooks/useEditorState';
import {RequiredTaggingEditorState} from '../state/requiredTaggingEditorState';
import {RequiredTaggingParamsEditorActionTypesEnum} from '../state/requiredTaggingParamsEditorActions';
import {requiredTaggingParamsEditorReducer} from '../state/requiredTaggingParamsEditorReducer';
import {
  createEmptyRequiredTaggingParameter,
  requiredTaggingParamsCreateInitialState,
} from '../state/requiredTaggingParamsEditorState';

/*
 * Types.
 */

type OnUpdateActionParameter = (
  predicate: PredicateOf<ClientRequiredTaggingParameter>,
  update: (requiredTaggingParameter: ClientRequiredTaggingParameter) => ClientRequiredTaggingParameter,
) => void;
type OnDeleteActionParameter = HandlerOf<PredicateOf<ClientRequiredTaggingParameter>>;

export interface RequiredTaggingEditorActions {
  onUpdateRuleName: HandlerOf<string>;

  onValidation: EditorStateActions['onValidation'];
  onSetHiddenErrors: EditorStateActions['onSetHiddenErrors'];

  onAddCondition: EditorStateActions['onAddCondition'];
  onUpdateCondition: EditorStateActions['onUpdateCondition'];
  onDeleteCondition: EditorStateActions['onDeleteCondition'];

  onAddActionParameter: Handler;
  onUpdateActionParameter: OnUpdateActionParameter;
  onDeleteActionParameter: OnDeleteActionParameter;
}

interface UseRequiredTaggingEditorStateReturnType extends RequiredTaggingEditorActions {
  requiredTaggingEditorState: RequiredTaggingEditorState;
}

/*
 * Hook.
 */

export function useRequiredTaggingEditorState(
  initialRequiredTaggingBundle: RuleBundleModel | undefined,
  customFields: CustomFields,
  ruleIntegration: RuleIntegration,
  namespace?: string,
): UseRequiredTaggingEditorStateReturnType {
  const maybeInitialRule = maybeBuildInitialRuleFromInitialBundle(initialRequiredTaggingBundle, namespace);

  const initialParameters = initialRequiredTaggingBundle?.rawDefinition?.requiredTaggingParams;
  const initialState = requiredTaggingParamsCreateInitialState(initialParameters);

  const [requiredTaggingEditorState, dispatch] = useReducer(requiredTaggingParamsEditorReducer, initialState);

  // we hide errors only if this is a recipe
  const maybeHiddenErrors = initialParameters
    ? undefined
    : initialState.requiredTaggingParamsState.map((param) => param.clientId);

  const {
    ruleEditorState,
    onUpdateRuleName,
    onAddCondition,
    onUpdateCondition,
    onDeleteCondition,
    onValidation,
    onSetHiddenErrors,
  } = useEditorState(
    maybeInitialRule,
    customFields,
    ruleIntegration,
    undefined,
    namespace,
    undefined,
    maybeHiddenErrors,
  );

  const onAddActionParameter = () => {
    const parameter = createEmptyRequiredTaggingParameter();
    dispatch({type: RequiredTaggingParamsEditorActionTypesEnum.ADD_PARAMETER, parameter});
    onSetHiddenErrors([parameter.clientId]);
  };

  const onUpdateActionParameter = (
    predicate: PredicateOf<ClientRequiredTaggingParameter>,
    update: (requiredTaggingParameter: ClientRequiredTaggingParameter) => ClientRequiredTaggingParameter,
  ) => {
    dispatch({type: RequiredTaggingParamsEditorActionTypesEnum.UPDATE_PARAMETER, update, predicate});
  };

  const onDeleteActionParameter = (predicate: PredicateOf<ClientRequiredTaggingParameter>) => {
    dispatch({type: RequiredTaggingParamsEditorActionTypesEnum.DELETE_PARAMETER, predicate});
  };

  return {
    requiredTaggingEditorState: {
      ...ruleEditorState,
      ...requiredTaggingEditorState,
    },

    onValidation,
    onSetHiddenErrors,
    onUpdateRuleName,

    onAddCondition,
    onUpdateCondition,
    onDeleteCondition,

    onAddActionParameter,
    onUpdateActionParameter,
    onDeleteActionParameter,
  };
}

function maybeBuildInitialRuleFromInitialBundle(
  initialRequiredTaggingBundle: RuleBundleModel | undefined,
  namespace?: string,
): PartialRuleModelStrict | undefined {
  if (!initialRequiredTaggingBundle) {
    return undefined;
  }

  return {
    id: initialRequiredTaggingBundle.id,
    active: true,
    name: initialRequiredTaggingBundle.name,
    compoundConditions: initialRequiredTaggingBundle.rawDefinition?.conditions,
    bundle: initialRequiredTaggingBundle,
    namespace,
  };
}
